import { FC } from 'react';

import { useRouter } from '@eneco/next';
import { Box, Button, Heading, RadioCard, RadioGroup, Stack, Text } from '@eneco/sparky';
import { useTracking } from '@eneco/tracking';
import { useContent } from '@sitecore/common';
import { OrderManagementRendering } from '@sitecore/types/OrderManagement';
import { signIn } from 'next-auth/react';
import { Controller, useFormContext } from 'react-hook-form';

import { OrderManagementFormSchema } from '../types/orderManagementSchema';
import { FlowChildProps, orderManagementSteps } from '../types/OrderManagementSteps';
import { getTrackableFormValues } from '../utils/getTrackableFormValues';

export const MyEnecoAccount: FC<FlowChildProps> = ({ nextMainStep }) => {
  const { fields } = useContent<OrderManagementRendering>();
  const { trackEventBE } = useTracking();
  const { activePath } = useRouter();

  const options = [
    {
      label: fields.myEnecoAccountStep.yesLabel.value,
      description: fields.myEnecoAccountStep.yesText.value,
    },
    {
      label: fields.myEnecoAccountStep.noLabel.value,
      description: fields.myEnecoAccountStep.noText.value,
    },
  ];

  const {
    control,
    getValues,
    formState: { errors },
    trigger,
  } = useFormContext<OrderManagementFormSchema>();

  const onNextClick = async () => {
    const isValid = await trigger('myEnecoAccount');
    if (!isValid) return;
    if (getValues('myEnecoAccount') === fields.myEnecoAccountStep.yesLabel.value) {
      const queryParams = new URLSearchParams(window.location.search);
      return signIn('okta', {
        callbackUrl: `${activePath}?${queryParams.toString()}#${orderManagementSteps.personalDetailsStep}`,
      });
    }
    trackEventBE('be_om_select_account_option', {
      status: 'click',
      data: getTrackableFormValues(getValues()),
    });
    nextMainStep();
  };

  return (
    <Stack gap="10" alignX="start">
      <Heading as="h1" size="M">
        {fields.myEnecoAccountStep.title.value}
      </Heading>

      <Controller
        control={control}
        name="myEnecoAccount"
        render={({ field }) => (
          <RadioGroup
            name="myEnecoAccount"
            aria-labelledby="myEnecoAccount"
            isFullWidth
            value={field.value}
            onValueChange={field.onChange}
            error={errors.myEnecoAccount?.message}>
            {options.map(option => (
              <RadioCard key={option.label} value={option.label} ariaLabelledby={option.label}>
                <Box padding="4">
                  <Stack direction="row" alignY="center" gap="3">
                    <RadioCard.Indicator />
                    <Stack>
                      <Text id={option.label} weight="bold">
                        {option.label}
                      </Text>
                      <Text>{option.description}</Text>
                    </Stack>
                  </Stack>
                </Box>
              </RadioCard>
            ))}
          </RadioGroup>
        )}
      />

      <Button onClick={onNextClick}>{fields.navigation.nextButtonLabel.value}</Button>
    </Stack>
  );
};
